import {
  type AxisScaleConfig,
  type AxisSeriesValues,
  type AxisValuesInput,
  type DualAxisScaleConfig,
  type DualAxisScaleOptions,
} from './dual-axis-scale.def.ts'

const DEFAULT_MIN_SPLIT_NUMBER = 4
const DEFAULT_MAX_SPLIT_NUMBER = 8
const DEFAULT_MIN_INTERVAL = 0.01
const MAX_AXIS_DECIMAL_PLACES = 2
const NICE_INTERVAL_FACTORS = [1, 2, 2.5, 4, 5, 8, 10]

export type {
  AxisScaleConfig,
  AxisSeriesValues,
  AxisValuesInput,
  DualAxisScaleConfig,
  DualAxisScaleOptions,
} from './dual-axis-scale.def.ts'

function getNiceInterval(rawInterval: number, minInterval: number): number {
  if (!Number.isFinite(rawInterval) || rawInterval <= 0) {
    return minInterval
  }

  // Snap intervals to human-friendly steps so the axis stays readable.
  const exponent = Math.floor(Math.log10(rawInterval))
  const magnitude = 10 ** exponent
  const normalized = rawInterval / magnitude
  const factor =
    NICE_INTERVAL_FACTORS.find((value) => normalized <= value) ?? 10

  return Math.max(factor * magnitude, minInterval)
}

function roundAxisValue(value: number): number {
  if (!Number.isFinite(value)) {
    return value
  }

  return Number(value.toFixed(MAX_AXIS_DECIMAL_PLACES))
}

function normalizeAxisScaleConfig(scale: AxisScaleConfig): AxisScaleConfig {
  return {
    ...scale,
    // Limit decimal noise from floating-point math so tick labels do not get too long.
    min: roundAxisValue(scale.min),
    max: roundAxisValue(scale.max),
    interval: roundAxisValue(scale.interval),
  }
}

function getAxisUsageScore(
  min: number,
  max: number,
  dataMin: number,
  dataMax: number,
  splitNumber: number
): number {
  const axisSpan = max - min
  const dataSpan = dataMax - dataMin

  if (axisSpan <= 0) {
    return Number.NEGATIVE_INFINITY
  }

  // Prefer candidates that use the available axis space well without adding too many grid rows.
  const coverageScore = dataSpan / axisSpan
  const extraSpaceScore = -(max - dataMax + (dataMin - min)) / axisSpan

  return coverageScore * 100 + extraSpaceScore * 10 - splitNumber * 0.05
}

function getZeroSafeMinMax(values: number[]): {
  rawMin: number
  rawMax: number
} {
  const finiteValues = values.filter((value) => Number.isFinite(value))

  return {
    // Force zero into the range so both axes can share the same baseline.
    rawMin: finiteValues.length > 0 ? Math.min(...finiteValues, 0) : 0,
    rawMax: finiteValues.length > 0 ? Math.max(...finiteValues, 0) : 0,
  }
}

function isAxisSeriesValuesArray(
  values: AxisValuesInput
): values is AxisSeriesValues[] {
  return values.length > 0 && typeof values[0] === 'object'
}

function normalizeAxisValuesInput(
  values: AxisValuesInput,
  useEnabledSeriesOnly = false
): number[] {
  if (!isAxisSeriesValuesArray(values)) {
    return values
  }

  const enabledSeries = useEnabledSeriesOnly
    ? values.filter((series) => series.enabled !== false)
    : values

  // Secondary-axis scaling should follow only the series the user can currently see.
  return enabledSeries.flatMap((series) => series.values)
}

function hasVisibleAxisSeries(values: AxisValuesInput): boolean {
  if (!isAxisSeriesValuesArray(values)) {
    return values.length > 0
  }

  return values.some((series) => series.enabled !== false)
}

function getBetterAxisScaleCandidate(
  candidate: AxisScaleConfig,
  rawMin: number,
  rawMax: number,
  bestScale: AxisScaleConfig | null,
  bestScore: number
): {
  bestScaleResult: AxisScaleConfig | null
  bestScoreResult: number
} {
  const score = getAxisUsageScore(
    candidate.min,
    candidate.max,
    rawMin,
    rawMax,
    candidate.splitNumber
  )

  return {
    bestScaleResult: score > bestScore ? candidate : bestScale,
    bestScoreResult: score > bestScore ? score : bestScore,
  }
}

function getBestAxisScaleConfig(
  values: number[],
  options: DualAxisScaleOptions = {}
): AxisScaleConfig {
  const minSplitNumber = options.minSplitNumber ?? DEFAULT_MIN_SPLIT_NUMBER
  const maxSplitNumber = options.maxSplitNumber ?? DEFAULT_MAX_SPLIT_NUMBER
  const minInterval = options.minInterval ?? DEFAULT_MIN_INTERVAL
  const { rawMin, rawMax } = getZeroSafeMinMax(values)

  if (rawMin === 0 && rawMax === 0) {
    return {
      min: -1,
      max: 1,
      interval: 1,
      splitNumber: 2,
      negativeSegments: 1,
      positiveSegments: 1,
    }
  }

  let bestScale: AxisScaleConfig | null = null
  let bestScore = Number.NEGATIVE_INFINITY

  // Try several grid counts and, when data crosses zero, several zero-line positions.
  for (
    let splitNumber = minSplitNumber;
    splitNumber <= maxSplitNumber;
    splitNumber++
  ) {
    if (rawMin >= 0 || rawMax <= 0) {
      const isRawMin = rawMin >= 0
      const interval = getNiceInterval(
        (isRawMin ? rawMax : Math.abs(rawMin)) / splitNumber,
        minInterval
      )
      const candidate = isRawMin
        ? {
            min: 0,
            max: interval * splitNumber,
            interval,
            splitNumber,
            negativeSegments: 0,
            positiveSegments: splitNumber,
          }
        : {
            min: -interval * splitNumber,
            max: 0,
            interval,
            splitNumber,
            negativeSegments: splitNumber,
            positiveSegments: 0,
          }

      const result = getBetterAxisScaleCandidate(
        candidate,
        rawMin,
        rawMax,
        bestScale,
        bestScore
      )
      bestScale = result.bestScaleResult
      bestScore = result.bestScoreResult

      continue
    }

    for (
      let negativeSegments = 1;
      negativeSegments < splitNumber;
      negativeSegments++
    ) {
      const positiveSegments = splitNumber - negativeSegments
      const interval = getNiceInterval(
        Math.max(
          Math.abs(rawMin) / negativeSegments,
          rawMax / positiveSegments
        ),
        minInterval
      )
      const candidate = {
        min: -negativeSegments * interval,
        max: positiveSegments * interval,
        interval,
        splitNumber,
        negativeSegments,
        positiveSegments,
      }
      const result = getBetterAxisScaleCandidate(
        candidate,
        rawMin,
        rawMax,
        bestScale,
        bestScore
      )
      bestScale = result.bestScaleResult
      bestScore = result.bestScoreResult
    }
  }

  return normalizeAxisScaleConfig(
    bestScale ?? {
      min: -1,
      max: 1,
      interval: 1,
      splitNumber: 2,
      negativeSegments: 1,
      positiveSegments: 1,
    }
  )
}

function getAlignedAxisScaleConfig(
  values: AxisValuesInput,
  baseAxis: Pick<AxisScaleConfig, 'splitNumber' | 'negativeSegments'>,
  options: Pick<DualAxisScaleOptions, 'minInterval'> = {}
): AxisScaleConfig {
  const minInterval = options.minInterval ?? DEFAULT_MIN_INTERVAL
  const normalizedValues = normalizeAxisValuesInput(values, true)
  const { rawMin, rawMax } = getZeroSafeMinMax(normalizedValues)
  const { splitNumber, negativeSegments } = baseAxis
  const positiveSegments = splitNumber - negativeSegments

  // Reuse the primary axis grid structure so both sides keep the same grid count and zero-line row.
  if (negativeSegments === 0) {
    const interval = getNiceInterval(rawMax / splitNumber, minInterval)

    return normalizeAxisScaleConfig({
      min: 0,
      max: interval * splitNumber,
      interval,
      splitNumber,
      negativeSegments,
      positiveSegments,
    })
  }

  if (positiveSegments === 0) {
    const interval = getNiceInterval(
      Math.abs(rawMin) / splitNumber,
      minInterval
    )

    return normalizeAxisScaleConfig({
      min: -interval * splitNumber,
      max: 0,
      interval,
      splitNumber,
      negativeSegments,
      positiveSegments,
    })
  }

  const interval = getNiceInterval(
    Math.max(
      Math.abs(rawMin) / negativeSegments,
      rawMax / positiveSegments,
      minInterval
    ),
    minInterval
  )

  return normalizeAxisScaleConfig({
    min: -negativeSegments * interval,
    max: positiveSegments * interval,
    interval,
    splitNumber,
    negativeSegments,
    positiveSegments,
  })
}

export function getAlignedDualAxisScaleConfig(
  primaryValues: number[],
  secondaryValues: AxisValuesInput,
  options: DualAxisScaleOptions = {}
): DualAxisScaleConfig {
  // The primary axis decides the grid layout; the secondary axis only adapts its numeric scale to it.
  const primaryAxis = getBestAxisScaleConfig(primaryValues, options)
  const secondaryAxisVisible = hasVisibleAxisSeries(secondaryValues)
  const secondaryAxis = getAlignedAxisScaleConfig(
    secondaryValues,
    primaryAxis,
    {
      minInterval: options.minInterval,
    }
  )

  return {
    primaryAxis,
    secondaryAxis,
    secondaryAxisVisible,
  }
}
